import {isAnyOf} from '@reduxjs/toolkit'

import {getDeliveryAddressFee} from '@web/actions/deliveryInfo'
import {getIntegratedDiscount} from '@web/actions/orderSheetDiscount'
import {getSelectProductDiscount} from '@web/actions/productCoupon'
import {fetchExpectedRewardPoint} from '@web/actions/rewardPoint'
import {
    CouponFailReasons,
    DeliveryFailAlertReasons,
    DeliveryNotOnSaleReasons,
    GiftSendCodes,
    OrderFailureType,
    ReasonType,
    ReasonTypeType,
    ReloadNotAllowedReason,
} from '@web/core/constants'
import {ErrorState} from '@web/slices/error/slice'
import makeSlice from '@web/slices/error/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'

const isRejectedValidationFailed = isAnyOf(
    getDeliveryAddressFee.rejected,
    fetchExpectedRewardPoint.rejected,
    getSelectProductDiscount.rejected,
)

const isFailureCase = (reasonType: string) => {
    const FailureCaseBlackList: string[] = [
        ReasonType.ORDER_ALREADY_CREATED,
        ...DeliveryNotOnSaleReasons,
        ...DeliveryFailAlertReasons,
        ...CouponFailReasons,
    ]
    return !FailureCaseBlackList.includes(reasonType)
}

const slice = makeSlice({
    extraReducers(builder) {
        builder
            .addCase(fetchExpectedRewardPoint.rejected, (state, {payload}) => {
                if (payload?.code === ReasonType.GET_REWARD_POLICY_FAILED) {
                    state.exceptionCodeMap = {
                        GET_REWARD_POLICY_FAILED: {
                            type: OrderFailureType.GET_REWARD_POLICY_FAILED,
                            code: ReasonType.GET_REWARD_POLICY_FAILED,
                            message: payload?.validationFailure?.fallbackMessage,
                        },
                    }
                } else if (payload?.validationFailure?.reasonType === ReasonType.INVALID_SELECTED_PRODUCT_COUPON) {
                    state.exceptionCodeMap = {
                        INVALID_SELECTED_PRODUCT_COUPON: {
                            type: ReasonType.INVALID_SELECTED_PRODUCT_COUPON,
                            code: ReasonType.INVALID_SELECTED_PRODUCT_COUPON,
                            message: payload.validationFailure.fallbackMessage,
                        },
                    }
                }
            })
            .addCase(initOrderSheetByAPI.rejected, (state, {payload}) => {
                state.failureCase = (function (): ErrorState['failureCase'] {
                    const code = payload?.code ?? ''

                    const fallbackMessage = payload?.validationFailure?.fallbackMessage ?? ''
                    if (GiftSendCodes.includes(code)) {
                        return {
                            type: OrderFailureType.BY_REDIRECT,
                            code: '',
                            message: '',
                        }
                    }
                    // 페이머니 회원조회 실패
                    if (code === ReasonType.GET_CHARGE_POINT_USER_FAILED) {
                        return {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.GET_CHARGE_POINT_USER_FAILED,
                            message: fallbackMessage,
                        }
                    }
                    if (code === ReasonType.GET_REWARD_POLICY_FAILED) {
                        return {
                            type: OrderFailureType.GET_REWARD_POLICY_FAILED,
                            code: ReasonType.GET_REWARD_POLICY_FAILED,
                            message: fallbackMessage,
                        }
                    }
                    if (code === 'VALIDATION_FAILED') {
                        const reasonType = payload?.validationFailure.reasonType
                        if (reasonType === ReasonType.BLACK_CONSUMER || reasonType === ReasonType.BLACKCONSUMER) {
                            return {
                                type: OrderFailureType.REFUSED_PURCHASER,
                                code: ReasonType.BLACK_CONSUMER,
                                message: fallbackMessage,
                                ...(function () {
                                    if (
                                        payload?.validationFailure?.reasonAttributes &&
                                        'denyingMerchants' in payload.validationFailure.reasonAttributes
                                    ) {
                                        return {
                                            attributes: {
                                                merchants:
                                                    payload.validationFailure.reasonAttributes.denyingMerchants.map(
                                                        ({merchantName, customerCenterPhoneNumber}) => {
                                                            return {
                                                                name: merchantName,
                                                                tel: customerCenterPhoneNumber,
                                                            }
                                                        },
                                                    ),
                                            },
                                        }
                                    }
                                    return {}
                                })(),
                            }
                        }
                        if (reasonType === ReasonType.ORDER_ALREADY_CREATED) {
                            return {
                                type: OrderFailureType.ORDER_ALREADY_CREATED,
                                code: ReasonType.ORDER_ALREADY_CREATED,
                            }
                        }
                        if (ReloadNotAllowedReason.includes(reasonType as ReasonTypeType)) {
                            return {
                                type: OrderFailureType.BY_REDIRECT,
                                code,
                                message: '',
                            }
                        }
                    }
                    if (code === 'ETIMEDOUT_FAILED') {
                        return {
                            type: OrderFailureType.BY_REDIRECT,
                            code,
                            message: '',
                        }
                    }
                    if (code === 'GET_SSG_DEPOSIT_FAILED') {
                        return {
                            type: OrderFailureType.GET_SSG_DEPOSIT_FAILED,
                            code: 'GET_SSG_DEPOSIT_FAILED',
                            message: payload?.fallbackMessage,
                            ...(function (): Pick<ErrorState['failureCase'], 'attributes'> {
                                if (payload?.customerCenterPhoneNumber) {
                                    return {
                                        attributes: {
                                            customerCenterPhoneNumber: payload.customerCenterPhoneNumber,
                                        },
                                    }
                                }
                                return {}
                            })(),
                        }
                    }
                    if (code === ReasonType.ORDER_SHEET_NOT_FOUND) {
                        return {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.ORDER_SHEET_NOT_FOUND,
                            message: payload?.fallbackMessage,
                        }
                    }
                    if (payload && payload.code && payload.fallbackMessage) {
                        return {
                            type: OrderFailureType.UNKNOWN,
                            code: payload.code,
                            message: payload.fallbackMessage,
                        }
                    }
                    return {
                        type: OrderFailureType.UNKNOWN,
                        code: ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                    }
                })()
            })
            .addCase(getIntegratedDiscount.rejected, (state, {payload}) => {
                if (payload && payload.from === 'DELIVERY_RELOAD') {
                    if (payload?.code === 'VALIDATION_FAILED') {
                        const reasonType = payload?.validationFailure.reasonType
                        if (isFailureCase(reasonType)) {
                            state.failureCase = {
                                type: OrderFailureType.UNKNOWN,
                                code: reasonType ?? ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                                message: payload?.validationFailure.fallbackMessage,
                            }
                        }
                    } else if (payload.code === 'ORDER_SHEET_NOT_FOUND') {
                        state.failureCase = {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                        }
                    }
                }
            })
            .addMatcher(isRejectedValidationFailed, (state, {payload}) => {
                if (payload) {
                    if (payload.code === 'VALIDATION_FAILED') {
                        const reasonType = payload?.validationFailure.reasonType
                        if (isFailureCase(reasonType)) {
                            state.failureCase = {
                                type: OrderFailureType.UNKNOWN,
                                code: reasonType ?? ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                                message: payload?.validationFailure.fallbackMessage,
                            }
                        }
                    } else if (payload.code === 'ORDER_SHEET_NOT_FOUND') {
                        state.failureCase = {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                        }
                    }
                }
            })
    },
})

export const actions = slice.actions

export default slice.reducer
